import { defaultConfig } from 'app/config/environment';
import useAmplitude from 'core/hooks/useAmplitude';
import useSystemSettings from 'core/hooks/useSystemSettings';
import useNutrientLicenceKey from 'core/lite/hooks/useNutrientLicenceKey';
import usePdfJsViewerConfig from 'core/lite/hooks/usePdfJsViewerConfig';
import { useUser } from 'core/lite/hooks/useUser';
import { pageTitleFromName } from 'app/router/pageTitleUtils';
import * as coreEntities from 'core/store/index';
import { resolvePdfEngine } from 'core/utils/pdfEngine';
import { logViewerError } from 'core/utils/sentry';
import { getViewerContentName, getViewerPageHeading } from 'core/utils/viewerPageHeading';
import { isNativeAppWebview } from 'core/utils/utils';
import { PDF_ENGINE } from 'core/variables/constant';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

export function useViewer({ config, contentGuid, eventParams, guid, showHeader, shouldEnableFullWidth, shouldConsumption }) {
  const { sendAmplitudeEvent, AMPLITUDE_EVENT } = useAmplitude();
  const location = useLocation();
  const { enableNutrient, enableNutrientStandalone, enablePdfJs, nutrientLicence } = useSystemSettings();
  const currentCourse = useSelector((state) => coreEntities.courses.selectors.getCurrentCourse(state));
  const lang = useSelector((state) => coreEntities.i18n.selectors.getLanguage(state));
  const { authToken, user, isStudent } = useUser();
  const [isLoading, setIsLoading] = useState(true);
  const [contentName, setContentName] = useState('');
  const { t } = useTranslation();

  // Si el visor pasa a otro recurso sin desmontarse y ese no informa de su nombre, el h1 no se queda con el anterior.
  useEffect(() => {
    setContentName('');
  }, [contentGuid, guid]);
  const searchParams = new URLSearchParams(location.search);
  const contentGuidScorm = searchParams.get('bookGuid') || contentGuid || guid;
  const referenceId = searchParams.get('referenceId') || null;
  const jobGuidFromUrl = searchParams.get('jobGuid') || null;
  let itemScorm = null;
  let itemScormFL = null;
  const isApp = isNativeAppWebview();
  let isShowHeader = isApp ? true : showHeader;

  if (referenceId) {
    itemScormFL = referenceId;
    itemScorm = referenceId.split('_').slice(1).join('_');
  }

  const scormMetadata = {
    lessonItem: searchParams.get('scope') || null,
    personGuid: searchParams.get('personGuid') || null,
    contentGuid: contentGuidScorm,
    itemScorm: itemScorm,
    itemScormFL: itemScormFL,
  };

  useEffect(() => {
    if ((contentGuid || guid) && authToken && user && currentCourse?.guid) {
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
  }, [contentGuid, guid, currentCourse, lang, authToken, user]);

  const engine = resolvePdfEngine({ enablePdfJs, enableNutrient, enableNutrientStandalone });
  const {
    customHotspotsImages,
    isReady: isPdfJsConfigReady,
    skinConfig,
    toolbar: pdfConfig,
  } = usePdfJsViewerConfig({
    isEnabled: engine === PDF_ENGINE.PDFJS,
    toolbar: config?.pdfConfig,
  });
  const isNutrientStandalone = engine === PDF_ENGINE.NUTRIENT_STANDALONE;
  const nutrientLicenceKey = useNutrientLicenceKey({ isEnabled: isNutrientStandalone, licence: nutrientLicence });

  let pdfLibraryUse;

  let docConfig = {
    toolbar: pdfConfig,
    library: 'pdftron',
    canTeacherOrStudentDownload: true,
  };

  if (engine === PDF_ENGINE.PDFTRON) {
    docConfig.library = 'pdftron';
    pdfLibraryUse = 'pdftron';
  }

  if (engine === PDF_ENGINE.NUTRIENT) {
    let host = window.location.host.replace('backoffice.', '').replace('publisher.', '').replace('adminescuela.', '');
    if (window.location.host.includes('localhost')) {
      host = new URL(defaultConfig?.BASE_URL_).hostname;
    }
    docConfig.library = 'nutrient';
    docConfig.documentEngineUrl = `https://doc-eng.${host}/`;
    docConfig.documentEngineJWTUrl = `https://${host}/doc-eng-service/documents/DOCUMENT_GUID/token`;
    pdfLibraryUse = 'nutrient';
  }

  if (isNutrientStandalone) {
    docConfig.library = 'nutrient-standalone';
    pdfLibraryUse = 'nutrient-standalone';
    docConfig.licenceKey = nutrientLicenceKey;
  }

  if (engine === PDF_ENGINE.PDFJS) {
    docConfig.library = PDF_ENGINE.PDFJS;
    docConfig.customHotspotsImages = customHotspotsImages;
    pdfLibraryUse = PDF_ENGINE.PDFJS;
  }
  // Como la demo, el Viewer no monta hasta tener iconos y colores del programa; ambos fallan abiertos a los 5 s.
  const isViewerPending = engine === PDF_ENGINE.PDFJS && !isPdfJsConfigReady;

  function reportErrorSentry(params) {
    logViewerError(isStudent, location, params);
  }

  function goToBackViewer() {
    if (isApp) {
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage('closeWebview');
      }
    } else {
      if (window.location.pathname.includes('group-resources-view--visor')) {
        window.history.back();
      } else if (window.opener) {
        window.close();
      } else {
        window.history.back();
      }
    }
  }

  function onContentLoaded(content) {
    setContentName(getViewerContentName(content));
    if (eventParams) {
      const extraParams = {
        content_guid: content.guid,
        content_type: content.type_guid,
        content_type_name: content.type_name,
        didactic_type_name: content.didacticTypes?.[0]?.name,
        from: eventParams?.from,
      };
      sendAmplitudeEvent(AMPLITUDE_EVENT.OPEN_CONTENT, extraParams, eventParams?.course);
    }
  }

  if (config?.disabledFetchData) {
    docConfig.disabledFetchData = {
      toc: true,
      hotspots: true,
      annotations: true,
    };
  }

  if (shouldEnableFullWidth) {
    docConfig.enableFullWidth = shouldEnableFullWidth;
  }

  return {
    authToken,
    courseGuid: currentCourse?.guid,
    lang,
    pdfConfig,
    controllersConfig: {
      consumption: shouldConsumption,
      canInteraction: !currentCourse?.isReadOnly,
    },
    user: { guid: user.guid, name: user.name, surname: user.lastname, role: user.role_guid },
    isLoading,
    docConfig,
    reportErrorSentry,
    pdfLibraryUse,
    isViewerPending,
    skinConfig,
    scormMetadata,
    goToBackViewer,
    isShowHeader,
    jobGuidFromUrl,
    onContentLoaded,
    ...getViewerPageHeading(contentName, currentCourse, t),
    pageTitle: pageTitleFromName(contentName),
  };
}
